import { useCallback, useEffect, useRef } from "react";
import { useScript } from "../hooks";

const APP_ID = "b0777961-ddbf-4732-9649-d9d4bcb0925b";

interface PylonChatSettings {
  app_id: string;
  email: string;
  name: string;
}

interface PylonWindow {
  chat_settings: PylonChatSettings;
}

declare global {
  interface Window {
    pylon: PylonWindow | undefined;
    Pylon?: (command: "hideChatBubble" | "showChatBubble") => void;
  }
}

interface UsePylonArgs {
  enabled: boolean;
  user?: {
    email: string;
    firstName: string;
    lastName: string;
  };
}

interface UsePylonReturn {
  hideChat: () => void;
  showChat: () => void;
}

/**
 * Hook to initialize and manage the Pylon chat widget.
 * Uses the shared useScript hook for script loading.
 * Returns functions to hide and show the chat bubble.
 */
export function usePylon({ enabled, user }: UsePylonArgs): UsePylonReturn {
  const scriptLoadedRef = useRef(false);

  // Only load the script if enabled and user is present
  const [, scriptError] = useScript(
    enabled && user ? `https://widget.usepylon.com/widget/${APP_ID}` : "",
    () => {
      scriptLoadedRef.current = true;
      // Type-safe access to window.pylon
      return window.pylon;
    },
  );

  // Set user settings when enabled and user data is available
  useEffect(() => {
    if (enabled && user) {
      window.pylon = {
        chat_settings: {
          app_id: APP_ID,
          email: user.email,
          name: `${user.firstName} ${user.lastName}`.trim(),
        },
      };
    }
  }, [enabled, user]);

  // Handle script errors
  useEffect(() => {
    if (scriptError) {
      // eslint-disable-next-line no-console
      console.error("Failed to load Pylon chat widget script:", scriptError);
    }
  }, [scriptError]);

  // Handle showing/hiding based on the enabled state
  useEffect(() => {
    // Only attempt to show/hide if the script has loaded
    if (scriptLoadedRef.current && window.Pylon) {
      if (enabled) {
        window.Pylon("showChatBubble");
      } else {
        window.Pylon("hideChatBubble");
      }
    }
  }, [enabled]);

  // Create callback functions to hide and show the chat bubble
  const hideChat = useCallback(() => {
    if (window.Pylon) {
      window.Pylon("hideChatBubble");
    }
  }, []);

  const showChat = useCallback(() => {
    if (window.Pylon) {
      window.Pylon("showChatBubble");
    }
  }, []);

  return { hideChat, showChat };
}
